import { useState } from "react";
import { toast } from "sonner";
import {
  AmenityCategory,
  AMENITY_CATEGORY_LABELS,
} from "@postnatal-care/shared-types";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  useCreateAmenity,
  type AmenityMasterEntity,
} from "@/hooks/useBusinesses";

interface AmenityCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (amenity: AmenityMasterEntity) => void;
}

export const AmenityCreateDialog = ({
  open,
  onOpenChange,
  onCreated,
}: AmenityCreateDialogProps) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState<AmenityCategory>(
    AmenityCategory.GENERAL_SERVICES,
  );
  const [isPremium, setIsPremium] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createAmenity = useCreateAmenity();

  const reset = () => {
    setName("");
    setCategory(AmenityCategory.GENERAL_SERVICES);
    setIsPremium(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return setError("Name is required");
    try {
      const created = await createAmenity.mutateAsync({
        name: name.trim(),
        category,
        isPremium,
      });
      toast.success(`Added "${created.name}"`);
      onCreated?.(created);
      reset();
      onOpenChange(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New amenity</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Breakfast included"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as AmenityCategory)}
              className="w-full h-10 px-3 py-2 text-sm rounded-md border border-input bg-background"
            >
              {Object.values(AmenityCategory).map((cat) => (
                <option key={cat} value={cat}>
                  {AMENITY_CATEGORY_LABELS[cat]?.en ?? cat}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2 py-2">
            <Switch
              id="isPremium"
              checked={isPremium}
              onCheckedChange={setIsPremium}
            />
            <Label htmlFor="isPremium">Is Premium / Unique?</Label>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createAmenity.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createAmenity.isPending}>
              {createAmenity.isPending ? "Creating…" : "Create amenity"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AmenityCreateDialog;
